import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommunityPage() {
  const [groups, setGroups] = useState([]);
  const [activeForm, setActiveForm] = useState(null); // 'investors' | 'founders_partners' | null
  const [submitted, setSubmitted] = useState(null);   // { group, vetted, join_link?, message }
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState({
    full_name: '', email: '', linkedin_url: '',
    role: '', organisation: '', country: '', notes: '',
  });

  useEffect(() => {
    document.title = 'Join the Community · SKI AXIS';
    axios.get(`${API}/community/groups`).then(r => setGroups(r.data?.groups || [])).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setSubmitting(true);
    try {
      const payload = { group: activeForm, ...form };
      // Strip empty optional fields
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const { data } = await axios.post(`${API}/community/request-invite`, payload);
      setSubmitted({ group: activeForm, ...data });
      setActiveForm(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Could not submit. Please check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const investors = groups.find(g => g.key === 'investors');
  const founders = groups.find(g => g.key === 'founders_partners');

  return (
    <div className="cmty-root" data-testid="community-page" style={{
      minHeight: '100vh', background: '#FAF7F0', color: '#0D1B2A',
      fontFamily: 'Georgia, "Times New Roman", serif',
    }}>
      {/* Reuse global landing-page CSS variables for consistency */}
      <style>{`
        .cmty-root { --ink:#FAF7F0; --ink2:#F4EFE6; --ink3:#FFFFFF; --cream:#0D1B2A; --mist:#3A4A5C; --gold:#C8A84B; --gold-line:rgba(200,168,75,.35); }
        .cmty-nav { display:flex; align-items:center; justify-content:space-between; padding:24px 48px; max-width:1380px; margin:0 auto; }
        .cmty-logo { font-family:'Cormorant Garamond',Georgia,serif; font-size:22px; font-weight:700; letter-spacing:-.01em; }
        .cmty-logo em { color:#C8A84B; font-style:italic; }
        .cmty-back { font-size:11px; letter-spacing:.18em; text-transform:uppercase; color:#3A4A5C; text-decoration:none; }
        .cmty-back:hover { color:#0D1B2A; }
        .cmty-hero { max-width:1380px; margin:0 auto; padding:64px 48px 48px; }
        .cmty-eyebrow { font-size:11px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:18px; display:flex; align-items:center; gap:14px; }
        .cmty-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#C8A84B; }
        .cmty-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:64px; line-height:1.04; font-weight:700; letter-spacing:-.022em; margin:0 0 22px; max-width:920px; }
        .cmty-title em { color:#C8A84B; font-style:italic; }
        .cmty-sub { font-size:17px; line-height:1.7; color:#3A4A5C; max-width:780px; margin:0; }
        @media (max-width:760px) { .cmty-nav { padding:18px 22px; } .cmty-hero { padding:40px 22px 24px; } .cmty-title { font-size:38px; } .cmty-sub { font-size:15px; } }

        .cmty-grid { max-width:1380px; margin:0 auto; padding:24px 48px 80px; display:grid; grid-template-columns:1fr 1fr; gap:32px; }
        @media (max-width:880px) { .cmty-grid { grid-template-columns:1fr; padding:16px 22px 40px; gap:20px; } }

        .cmty-card { padding:48px 44px; border:1px solid var(--gold-line); position:relative; overflow:hidden; transition:transform .35s ease, border-color .35s ease, box-shadow .35s ease; }
        .cmty-card:hover { transform:translateY(-4px); border-color:#C8A84B; box-shadow:0 18px 60px -28px rgba(13,27,42,.32); }
        .cmty-card.dark { background:linear-gradient(155deg,#0D1B2A 0%,#1A2F4A 100%); color:#FAF7F0; border:1px solid rgba(200,168,75,.4); }
        .cmty-card.cream { background:#FFFFFF; }
        .cmty-card-tier { font-size:10.5px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:16px; }
        .cmty-card-name { font-family:'Cormorant Garamond',Georgia,serif; font-size:34px; line-height:1.12; font-weight:700; letter-spacing:-.012em; margin:0 0 10px; }
        .cmty-card-name em { color:#C8A84B; font-style:italic; }
        .cmty-card-tag { font-style:italic; font-size:14.5px; line-height:1.55; opacity:.85; margin:0 0 22px; }
        .cmty-card-desc { font-size:14.5px; line-height:1.7; opacity:.9; margin:0 0 28px; }
        .cmty-card.dark .cmty-card-desc, .cmty-card.dark .cmty-card-tag { color:rgba(250,247,240,.85); }

        .cmty-rules { list-style:none; margin:0 0 32px; padding:0; }
        .cmty-rules li { font-size:13.5px; line-height:1.65; padding:7px 0 7px 26px; position:relative; opacity:.92; }
        .cmty-rules li::before { content:'→'; position:absolute; left:0; color:#C8A84B; font-weight:700; }
        .cmty-card.dark .cmty-rules li { color:rgba(250,247,240,.82); }

        .cmty-cta { display:inline-block; padding:14px 30px; font-size:11.5px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; text-decoration:none; cursor:pointer; border:none; transition:transform .25s ease, background .25s ease; font-family:Georgia,serif; }
        .cmty-cta-gold { background:#C8A84B; color:#0D1B2A; }
        .cmty-cta-gold:hover { background:#D4B65C; transform:translateY(-2px); }
        .cmty-cta-line-cream { background:transparent; color:#FAF7F0; border:1px solid #C8A84B; }
        .cmty-cta-line-cream:hover { background:rgba(200,168,75,.12); transform:translateY(-2px); }
        .cmty-cta-wa { background:#25D366; color:#FFFFFF; }
        .cmty-cta-wa:hover { background:#1FB755; transform:translateY(-2px); }

        /* Modal */
        .cmty-modal-back { position:fixed; inset:0; background:rgba(13,27,42,.72); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; padding:20px; z-index:200; animation:fadein .25s ease; }
        @keyframes fadein { from { opacity:0; } to { opacity:1; } }
        .cmty-modal { background:#FFFFFF; max-width:580px; width:100%; padding:44px 44px 36px; border:1px solid var(--gold-line); max-height:92vh; overflow-y:auto; }
        @media (max-width:560px) { .cmty-modal { padding:30px 22px 22px; } }
        .cmty-modal-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:30px; line-height:1.15; font-weight:700; letter-spacing:-.012em; margin:0 0 6px; }
        .cmty-modal-title em { color:#C8A84B; font-style:italic; }
        .cmty-modal-sub { font-size:13.5px; line-height:1.6; color:#3A4A5C; margin:0 0 24px; }
        .cmty-field { display:block; margin-bottom:16px; }
        .cmty-field-lbl { font-size:11px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; color:#3A4A5C; margin-bottom:6px; display:block; }
        .cmty-field input, .cmty-field textarea, .cmty-field select { width:100%; padding:11px 14px; font-size:14px; font-family:Georgia,serif; color:#0D1B2A; background:#FAF7F0; border:1px solid rgba(13,27,42,.18); border-radius:0; outline:none; transition:border-color .2s ease; box-sizing:border-box; }
        .cmty-field input:focus, .cmty-field textarea:focus, .cmty-field select:focus { border-color:#C8A84B; }
        .cmty-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        @media (max-width:560px) { .cmty-row { grid-template-columns:1fr; } }
        .cmty-modal-actions { display:flex; gap:12px; align-items:center; margin-top:8px; }
        .cmty-cancel { background:transparent; border:none; font-size:11.5px; letter-spacing:.18em; text-transform:uppercase; color:#3A4A5C; cursor:pointer; padding:14px 18px; font-family:Georgia,serif; font-weight:700; }
        .cmty-err { background:rgba(214,48,49,.08); border-left:3px solid #D63031; color:#D63031; padding:10px 14px; font-size:13px; margin-bottom:14px; }

        /* Success card */
        .cmty-success { max-width:720px; margin:0 auto; background:#FFFFFF; border:1px solid var(--gold-line); padding:48px 44px; text-align:center; }
        .cmty-success-tier { font-size:11px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:14px; }
        .cmty-success-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:36px; line-height:1.18; font-weight:700; letter-spacing:-.012em; margin:0 0 12px; }
        .cmty-success-title em { color:#C8A84B; font-style:italic; }
        .cmty-success-msg { font-size:15px; line-height:1.7; color:#3A4A5C; margin:0 0 28px; }
        .cmty-success-actions { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }
      `}</style>

      {/* Top Nav */}
      <div className="cmty-nav">
        <Link to="/" className="cmty-logo" data-testid="community-back-home"><strong>SKI</strong> <em>AXIS</em></Link>
        <Link to="/" className="cmty-back" data-testid="community-back">← Back</Link>
      </div>

      {/* Hero */}
      <section className="cmty-hero">
        <div className="cmty-eyebrow">SKI Axis · WhatsApp Community</div>
        <h1 className="cmty-title">Where the room <em>actually</em> talks.</h1>
        <p className="cmty-sub">
          Two private WhatsApp communities — one for vetted institutional capital, one for SKI Axis founders, speakers and free zone partners.
          High signal. No cold pitching. Built for the operators inside the UAE corridor.
        </p>
      </section>

      {/* Success state — overrides grid */}
      {submitted && (
        <section style={{ padding: '0 22px 80px' }}>
          <div className="cmty-success" data-testid="community-success">
            <div className="cmty-success-tier">{submitted.status === 'approved' ? 'Approved · Welcome' : 'Request Received'}</div>
            <h2 className="cmty-success-title">
              {submitted.status === 'approved' ? <>You're <em>in</em>.</> : <>Request <em>received</em>.</>}
            </h2>
            <p className="cmty-success-msg">{submitted.message}</p>
            <div className="cmty-success-actions">
              {submitted.join_link && (
                <a href={submitted.join_link} target="_blank" rel="noopener noreferrer" className="cmty-cta cmty-cta-wa" data-testid="community-direct-join">
                  Open WhatsApp →
                </a>
              )}
              <Link to="/events/invest-uae-forum-2026" className="cmty-cta cmty-cta-gold" data-testid="community-forum-cta">Reserve Forum Seat · AED 149 →</Link>
              <button onClick={() => { setSubmitted(null); setForm({ full_name: '', email: '', linkedin_url: '', role: '', organisation: '', country: '', notes: '' }); }} className="cmty-cancel" data-testid="community-success-back">← Back to groups</button>
            </div>
          </div>
        </section>
      )}

      {/* Groups grid */}
      {!submitted && (
        <section className="cmty-grid">
          {/* Investors Circle */}
          <div className="cmty-card dark" data-testid="community-card-investors">
            <div className="cmty-card-tier">Group I · Vetted Access</div>
            <div className="cmty-card-name">SKI AXIS <em>Investors Circle</em></div>
            <p className="cmty-card-tag">{investors?.tagline || 'Vetted institutional capital · UK + UAE + Global'}</p>
            <p className="cmty-card-desc">{investors?.description || 'A private WhatsApp Community for accredited investors, family offices, VCs, angels, and institutional principals deploying into the UAE corridor.'}</p>
            <ul className="cmty-rules">
              <li>Family offices, VCs, angels, institutional capital only</li>
              <li>LinkedIn-verified, manually reviewed by Dr. Neha + Dr. Ross</li>
              <li>Deal flow welcomed with context — no cold pitches</li>
              <li>Forum-priority access · Cohort 1 demo day intro list</li>
            </ul>
            <button onClick={() => setActiveForm('investors')} className="cmty-cta cmty-cta-line-cream" data-testid="community-apply-investors">
              Apply for Vetted Access →
            </button>
          </div>

          {/* Founders & Partners */}
          <div className="cmty-card cream" data-testid="community-card-founders">
            <div className="cmty-card-tier">Group II · Open Access</div>
            <div className="cmty-card-name">Founders &amp; <em>Partners Hub</em></div>
            <p className="cmty-card-tag">{founders?.tagline || 'Cohort founders · Speakers · Free zone partners · Faculty'}</p>
            <p className="cmty-card-desc">{founders?.description || 'Open community for SKI Axis Cohort 1 applicants, confirmed founders, forum speakers, free zone partners, and platform faculty.'}</p>
            <ul className="cmty-rules">
              <li>Cohort 1 applicants and accepted founders</li>
              <li>Forum speakers, faculty, and free zone partners</li>
              <li>Programme updates, masterclass drops, IFZA news</li>
              <li>Founder-to-founder support · live Q&amp;A · resources</li>
            </ul>
            {founders?.join_link ? (
              <a href={founders.join_link} target="_blank" rel="noopener noreferrer" className="cmty-cta cmty-cta-wa" data-testid="community-join-founders">
                Join WhatsApp Community →
              </a>
            ) : (
              <button onClick={() => setActiveForm('founders_partners')} className="cmty-cta cmty-cta-gold" data-testid="community-apply-founders">
                Request Invite →
              </button>
            )}
          </div>
        </section>
      )}

      {/* Application Modal */}
      {activeForm && (
        <div className="cmty-modal-back" onClick={() => setActiveForm(null)} data-testid="community-modal-backdrop">
          <div className="cmty-modal" onClick={(e) => e.stopPropagation()} data-testid="community-modal">
            <div className="cmty-card-tier" style={{ marginBottom: 8 }}>
              {activeForm === 'investors' ? 'Investors Circle · Vetted Access' : 'Founders & Partners Hub · Request Invite'}
            </div>
            <h3 className="cmty-modal-title">
              {activeForm === 'investors' ? <>Tell us a little about <em>your capital</em>.</> : <>Tell us where you <em>fit</em>.</>}
            </h3>
            <p className="cmty-modal-sub">
              {activeForm === 'investors'
                ? 'We review every request personally. You will hear from Dr. Neha or Dr. Ross within 24 hours.'
                : 'You will receive your WhatsApp invite link by email within minutes.'}
            </p>

            {err && <div className="cmty-err" data-testid="community-form-error">{err}</div>}

            <form onSubmit={onSubmit}>
              <div className="cmty-row">
                <label className="cmty-field">
                  <span className="cmty-field-lbl">Full Name *</span>
                  <input type="text" name="full_name" required value={form.full_name} onChange={onChange} data-testid="community-input-name" />
                </label>
                <label className="cmty-field">
                  <span className="cmty-field-lbl">Email *</span>
                  <input type="email" name="email" required value={form.email} onChange={onChange} data-testid="community-input-email" />
                </label>
              </div>

              <label className="cmty-field">
                <span className="cmty-field-lbl">LinkedIn URL {activeForm === 'investors' ? '*' : ''}</span>
                <input type="url" name="linkedin_url" required={activeForm === 'investors'} placeholder="https://linkedin.com/in/..." value={form.linkedin_url} onChange={onChange} data-testid="community-input-linkedin" />
              </label>

              <div className="cmty-row">
                <label className="cmty-field">
                  <span className="cmty-field-lbl">Role *</span>
                  <select name="role" required value={form.role} onChange={onChange} data-testid="community-input-role">
                    <option value="">Select…</option>
                    {activeForm === 'investors' ? (
                      <>
                        <option>Family Office</option>
                        <option>VC / Fund Manager</option>
                        <option>Angel Investor</option>
                        <option>HNWI / Private Investor</option>
                        <option>Institutional / Sovereign</option>
                        <option>Other</option>
                      </>
                    ) : (
                      <>
                        <option>Founder · Cohort 1 Applicant</option>
                        <option>Founder · General</option>
                        <option>Forum Speaker</option>
                        <option>Faculty / Mentor</option>
                        <option>Free Zone Partner</option>
                        <option>Other</option>
                      </>
                    )}
                  </select>
                </label>
                <label className="cmty-field">
                  <span className="cmty-field-lbl">Organisation</span>
                  <input type="text" name="organisation" value={form.organisation} onChange={onChange} data-testid="community-input-org" />
                </label>
              </div>

              <label className="cmty-field">
                <span className="cmty-field-lbl">Country</span>
                <input type="text" name="country" placeholder="UK · UAE · India · USA…" value={form.country} onChange={onChange} data-testid="community-input-country" />
              </label>

              <label className="cmty-field">
                <span className="cmty-field-lbl">{activeForm === 'investors' ? 'Mandate / typical ticket size' : 'A line about you'}</span>
                <textarea name="notes" rows={3} maxLength={400} placeholder={activeForm === 'investors' ? 'e.g. Family office · UAE real estate + AI tickets $250K–$2M · Series A focus' : 'e.g. SaaS founder, post-Seed, looking to register in IFZA'} value={form.notes} onChange={onChange} data-testid="community-input-notes" />
              </label>

              <div className="cmty-modal-actions">
                <button type="submit" disabled={submitting} className="cmty-cta cmty-cta-gold" data-testid="community-submit">
                  {submitting ? 'Submitting…' : (activeForm === 'investors' ? 'Submit for Review →' : 'Request Invite →')}
                </button>
                <button type="button" onClick={() => setActiveForm(null)} className="cmty-cancel" data-testid="community-modal-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
